'use client';

import { useMemo } from 'react';

import { BigNum, BN } from '@drift-labs/sdk';
import { getApyFromApr } from 'src/utils/borrowLend';
import { AprDetails } from './earn/types';
import useBorrowLendDataForMarket from './useBorrowLendDataForMarket';
import { MarketId } from '@drift/common';
import { EarnProduct } from './useEarnProduct';
import { getDepositData } from 'src/utils/earn/getDepositData';
import { getEarnProductData } from 'src/utils/earn/getEarnProductData';
import { BorrowMarketData } from './useSyncBorrowLendBalances';
import { useMarketInterestData } from './earn/useMarketInterestData';

const getBorrowingData = (
	isMarketBased: boolean,
	borrowLendData: BorrowMarketData | undefined,
	marketAprBreakdowns: AprDetails[],
	earnProduct: EarnProduct | null
): ApyCalculationResult => {
	let baseApy = 0;
	let precision = new BN(6);

	if (isMarketBased && borrowLendData) {
		const baseBorrowApr = borrowLendData.borrowApr
			.mul(BigNum.from(100))
			.toNum();
		baseApy = getApyFromApr(baseBorrowApr, 'hourly');
		precision = borrowLendData.borrowApr.precision;
	}

	const borrowRewardBreakdowns = marketAprBreakdowns.filter(
		(apr: AprDetails) => apr.type === 'borrow-reward'
	);

	const borrowRewardApy = borrowRewardBreakdowns.reduce(
		(acc: number, curr: AprDetails) => {
			return acc + (curr.apy ?? 0);
		},
		0
	);

	const baseApyBN = BigNum.fromPrint(baseApy.toString(), precision);
	const borrowRewardApyBN = BigNum.fromPrint(
		borrowRewardApy.toString(),
		precision
	);

	const totalApyBN = baseApyBN.sub(borrowRewardApyBN);
	const totalApy = totalApyBN.toNum();

	// Generate chunks
	const chunks: ApyChunk[] = [];
	if (borrowRewardBreakdowns.length > 0) {
		chunks.push({
			title: 'Borrow APY',
			apyBreakdown: [
				{
					type: 'borrow',
					source: 'Borrow APY',
					apy: baseApy,
				},
				...borrowRewardBreakdowns,
			],
		});
	}

	const hasAtMostBorrowInterest =
		chunks.length === 0 ||
		(chunks.length === 1 &&
			chunks[0].apyBreakdown.length === 1 &&
			chunks[0].apyBreakdown[0].type === 'borrow');
	const showTooltip =
		!hasAtMostBorrowInterest && earnProduct?.productType !== 'amplify';

	const showRewardsIcon = borrowRewardBreakdowns.some((apr) =>
		apr.type.includes('reward')
	);

	return {
		baseApy,
		totalApy,
		totalApyBN,
		additionalBreakdowns: borrowRewardBreakdowns,
		nativeBreakdown: undefined,
		showTooltip,
		chunks,
		showRewardsIcon,
	};
};

export interface ApyCalculationResult {
	baseApy: number;
	totalApy: number;
	totalApyBN: BigNum;
	additionalBreakdowns: AprDetails[];
	nativeBreakdown?: AprDetails;
	showTooltip: boolean;
	chunks: ApyChunk[];
	showRewardsIcon: boolean;
}

export interface ApyChunk {
	title: string | undefined;
	apyBreakdown: AprDetails[];
	isPerpCollateralReward?: boolean;
}

export interface ApyBreakdownTooltipData {
	deposit: ApyCalculationResult;
	borrowing: ApyCalculationResult;
	// EarnProduct specific data
	relevantBreakdowns?: AprDetails[];
	baseSourceTitle?: string;
	baseApyType?: 'if' | 'lend';
	baseApySource?: string;
}

// Overload signatures for different usage patterns
export function useApyBreakdownTooltipData(
	marketIndex: number
): ApyBreakdownTooltipData;
export function useApyBreakdownTooltipData(
	earnProduct: EarnProduct
): ApyBreakdownTooltipData;

/**
 * Unified hook that provides calculated APY data and pre-formatted chunks for ApyBreakdownTooltipV2
 * Supports both market-based and EarnProduct-based usage patterns
 * Eliminates repetitive APY calculation logic across all components
 */
export function useApyBreakdownTooltipData(
	marketIndexOrEarnProduct: number | EarnProduct
): ApyBreakdownTooltipData {
	// Determine usage pattern
	const isMarketBased = typeof marketIndexOrEarnProduct === 'number';
	const isEarnProductBased = !isMarketBased;

	// Get market index for both usage patterns
	const marketIndex = isMarketBased
		? marketIndexOrEarnProduct
		: (marketIndexOrEarnProduct as EarnProduct).marketIndex;

	const interestData = useMarketInterestData({ marketIndex });
	const borrowLendData = useBorrowLendDataForMarket(
		MarketId.createSpotMarket(marketIndex ?? 0)
	);

	const earnProduct = isEarnProductBased
		? (marketIndexOrEarnProduct as EarnProduct)
		: null;

	const marketAprBreakdowns = useMemo(() => {
		if (!interestData) return [];
		return [
			...interestData.deposit.breakdown,
			...interestData.borrow.breakdown,
		];
	}, [interestData, marketIndex]);

	const perpsCollateralRewardDetails = useMemo(() => {
		return marketAprBreakdowns.find((apr) => apr.type === 'lend-oi-reward');
	}, [marketAprBreakdowns]);

	const earnProductData = useMemo(() => {
		if (!earnProduct) return null;
		return getEarnProductData(earnProduct, marketAprBreakdowns);
	}, [earnProduct, marketAprBreakdowns]);

	const depositData = useMemo(
		(): ApyCalculationResult =>
			getDepositData(
				marketAprBreakdowns,
				borrowLendData,
				earnProductData,
				perpsCollateralRewardDetails,
				earnProduct,
				isEarnProductBased,
				isMarketBased
			),
		[
			isMarketBased,
			isEarnProductBased,
			borrowLendData,
			earnProductData,
			marketAprBreakdowns,
			perpsCollateralRewardDetails,
		]
	);

	const borrowingData = getBorrowingData(
		isMarketBased,
		borrowLendData,
		marketAprBreakdowns,
		earnProduct
	);

	return {
		deposit: depositData,
		borrowing: borrowingData,
		// EarnProduct specific data
		relevantBreakdowns: earnProductData?.relevantBreakdowns,
		baseSourceTitle: earnProductData?.baseSourceTitle,
		baseApyType: earnProductData?.baseApyType,
		baseApySource: earnProductData?.baseApySource,
	};
}
